import React from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import MarketHeatmap from '../../components/ui/MarketHeatmap';
import WorkspaceTabs from '../../components/ui/WorkspaceTabs';
import { CRYPTO_TABS } from './cryptoTabs';

/**
 * Crypto workspace — Heatmap sub-page.
 * CoinGecko top-200 treemap + Top Gainers / Losers.
 * Data is fetched once by CryptoWorkspace and passed via Outlet context.
 */
export default function CryptoHeatmapPage() {
  const navigate = useNavigate();
  const { universe, quotes, loading, refresh } = useOutletContext();

  const categories = (() => {
    const uniq = [];
    const seen = new Set();
    universe.forEach(c => {
      if (c.category && !seen.has(c.category)) { uniq.push(c.category); seen.add(c.category); }
    });
    return uniq;
  })();

  const movers = (() => {
    const withChange = universe
      .map(item => {
        const q = quotes[item.ticker];
        if (!q?.currentPrice || !q?.previousClose) return null;
        const pct = ((q.currentPrice - q.previousClose) / q.previousClose) * 100;
        return { ...item, quote: q, pct };
      })
      .filter(Boolean);
    return {
      gainers: [...withChange].sort((a, b) => b.pct - a.pct).slice(0, 5),
      losers:  [...withChange].sort((a, b) => a.pct - b.pct).slice(0, 5),
    };
  })();

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Crypto Heatmap</h1>
          <p style={s.subtitle}>
            Live overview of {universe.length} top cryptocurrencies grouped by category.
            Click any cell to view the full chart.
          </p>
        </div>
        <button style={s.refreshBtn} onClick={refresh} title="Refresh prices">&#8635; Refresh</button>
      </div>

      <WorkspaceTabs tabs={CRYPTO_TABS} />

      <div style={s.section}>
        <div style={s.sectionHeader}>
          <h2 style={s.sectionTitle}>Crypto Heatmap</h2>
          <span style={s.sectionMeta}>Daily change · sized by market cap</span>
        </div>
        <MarketHeatmap
          items={universe}
          quotes={quotes}
          loading={loading}
          groupKey="category"
          groups={categories}
          onSelect={(ticker) => navigate(`/crypto/${ticker}`)}
        />
      </div>

      {!loading && movers.gainers.length > 0 && (
        <div style={s.moversRow}>
          <MoversCard title="Top Gainers" items={movers.gainers} color="#22c55e" onSelect={(t) => navigate(`/crypto/${t}`)} />
          <MoversCard title="Top Losers"  items={movers.losers}  color="#f87171" onSelect={(t) => navigate(`/crypto/${t}`)} />
        </div>
      )}
    </div>
  );
}

function MoversCard({ title, items, color, onSelect }) {
  return (
    <div style={s.moversCard}>
      <h3 style={{ ...s.moversTitle, color }}>{title}</h3>
      <div style={s.moversList}>
        {items.map(it => (
          <button key={it.ticker} style={s.moverRow} onClick={() => onSelect(it.ticker)}>
            <span style={s.moverTicker}>{it.ticker.replace('-USD', '')}</span>
            <span style={s.moverName}>{it.quote?.shortName || it.name}</span>
            <span style={{ ...s.moverPct, color: it.pct >= 0 ? '#22c55e' : '#f87171' }}>
              {it.pct >= 0 ? '+' : ''}{it.pct.toFixed(2)}%
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

const s = {
  page: { padding: '0 0 48px' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' },
  title: { margin: 0, fontSize: '26px', fontWeight: 800, color: 'var(--text-primary)' },
  subtitle: { margin: '4px 0 0', fontSize: '14px', color: 'var(--text-secondary)', maxWidth: '720px' },
  refreshBtn: {
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px',
    padding: '7px 14px', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer',
  },
  section: { marginBottom: '32px' },
  sectionHeader: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' },
  sectionTitle: { margin: 0, fontSize: '15px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  sectionMeta: { fontSize: '12px', color: 'var(--text-secondary)', opacity: 0.7 },
  moversRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' },
  moversCard: { background: 'var(--surface-strong)', border: '1px solid var(--border)', borderRadius: '14px', padding: '16px' },
  moversTitle: { margin: '0 0 12px', fontSize: '13px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase' },
  moversList: { display: 'flex', flexDirection: 'column', gap: '4px' },
  moverRow: { display: 'grid', gridTemplateColumns: '60px 1fr auto', alignItems: 'center', gap: '10px', padding: '7px 8px', background: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer', textAlign: 'left' },
  moverTicker: { fontSize: '13px', fontWeight: 800, color: 'var(--text-primary)' },
  moverName:   { fontSize: '12px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  moverPct:    { fontSize: '13px', fontWeight: 700 },
};
